<template>
  <tbody class="field-tbody" @mouseleave="hoveredPath = null">
    <template v-for="row in rows" :key="row.key">
      <tr
        v-if="row.type === 'field'"
        :class="rowClass(row.depth)"
        @mouseenter="hoveredPath = row.path"
      >
        <td class="name-cell">
          <span
            v-for="line in connectorLines(row)"
            :key="line.key"
            class="tree-line"
            :style="line.style"
          />
          <div class="d-flex align-center" :style="{ paddingLeft: indentPx(row.depth) }">
            <span
              v-if="row.field.type === 'filter' || row.field.computed"
              class="field-state-icon"
            >
              <v-icon
                :icon="row.field.type === 'filter' ? 'mdi-filter-variant' : 'mdi-function-variant'"
                size="16"
              />
              <v-icon
                v-if="row.field.type === 'filter' && row.field.computed"
                class="handle-computed-badge"
                icon="mdi-function-variant"
                size="10"
              />
            </span>
            <quick-rename-input
              :value="row.field.name"
              @onChange="(e) => onBlurName(e, row.path)"
              density="compact"
              :max-length="30"
              :disabled="isWorkflowDisabled"
            />
          </div>
        </td>
        <td class="description-cell" :class="{ 'cell-empty': !row.field.description }">
          <span v-if="!row.field.description" class="cell-dash">
            -
          </span>
          <quick-rename-input
            :value="row.field.description || ''"
            @onChange="(e) => onBlurDescription(e, row.field, row.path)"
            density="compact"
            :max-length="MAX_FIELD_DESCRIPTION_LENGTH"
            :disabled="isWorkflowDisabled"
            class="field-description-input"
          />
        </td>
        <td class="entity-cell">
          <template v-if="row.depth >= maxDepth">
            <span class="cell-dash">
              -
            </span>
          </template>
          <template v-else-if="row.field.relatedEntityId && getRelatedEntity(row.field)">
            <div class="d-flex align-center ga-1">
              <select-entity
                :entityId="row.field.relatedEntityId"
                @onSelectEntity="(entity) => onSelectRelatedEntity(entity, row.path)"
              >
                <v-chip
                  density="compact"
                  size="small"
                  class="cursor-pointer entity-chip"
                  :closable="!isWorkflowDisabled"
                >
                  <v-icon start size="12">mdi-database</v-icon>
                  <span>
                    {{ getRelatedEntity(row.field).name }}
                  </span>
                  <template v-if="!isWorkflowDisabled" #close>
                    <v-icon
                      icon="mdi-close-circle"
                      color="#888"
                      :size="16"
                      @click.stop="onSelectRelatedEntity(null, row.path)"
                    />
                  </template>
                </v-chip>
              </select-entity>
              <toggle
                mandatory
                :value="row.field.cardinality || 'one-to-one'"
                :options="FIELD_CARDINALITY_OPTIONS"
                :disabled="isWorkflowDisabled"
                @update:modelValue="(c) => onSetCardinality(row.path, c)"
              />
            </div>
          </template>
          <template v-else>
            <span class="cell-dash">
              -
            </span>
            <div v-if="!isWorkflowDisabled" class="entity-hover-reveal">
              <select-entity
                @onSelectEntity="(entity) => onSelectRelatedEntity(entity, row.path)"
              >
                <v-chip density="compact" size="small" class="cursor-pointer entity-chip">
                  <v-icon start size="12">mdi-database</v-icon>
                  <span>
                    Select Entity
                  </span>
                </v-chip>
              </select-entity>
            </div>
          </template>
        </td>
        <td class="actions-cell">
          <query-field-actions
            v-if="!isWorkflowDisabled"
            :field="row.field"
            :hide-entity="row.depth >= maxDepth"
            @toggle-filter="onToggleFilter(row.path)"
            @toggle-computed="onToggleComputed(row.path)"
            @select-entity="(entity) => onSelectRelatedEntity(entity, row.path)"
            @delete="onDelete(row.path)"
            @update:cardinality="(c) => onSetCardinality(row.path, c)"
          />
        </td>
      </tr>
      <tr
        v-else-if="!isWorkflowDisabled"
        class="add-subfield-row"
        @mouseenter="hoveredPath = row.path"
      >
        <td colspan="4" class="add-subfield-cell">
          <div
            class="add-subfield-wrapper"
            :class="{ 'add-subfield-wrapper--visible': isAddVisible(row) }"
            :style="{ paddingLeft: addIndentPx(row.depth) }"
          >
            <v-btn
              rounded="lg"
              size="small"
              variant="flat"
              color="primary"
              @click="onAddSubField(row.path)"
            >
              + Add sub-field
            </v-btn>
          </div>
        </td>
      </tr>
    </template>
  </tbody>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import Toggle from '@/components/Toggle.vue'
import SelectEntity from '@/components/SelectEntity.vue'
import QuickRenameInput from '@/components/QuickRenameInput'
import QueryFieldActions from '@/components/QueryFieldActions.vue'
import useDomainModel from '@/composables/useDomainModel'
import useReadModelActions from '@/composables/useReadModelActions'
import { FIELD_CARDINALITY_OPTIONS, MAX_FIELD_DESCRIPTION_LENGTH } from '@/constants/schema'

const GUIDE = 20
const CELL_PADDING = 16

const props = defineProps({
  topField: { type: Object, required: true },
  fieldIndex: { type: Number, required: true },
  topFieldIsLast: { type: Boolean, default: false },
  maxDepth: { type: Number, default: 3 },
  schema: { type: Object, required: true },
  isWorkflowDisabled: { type: Boolean, default: false }
})

const store = useStore()

const {
  selectRelatedEntityAtSchemaPath,
  setSchemaFieldCardinalityAtPath,
  renameSchemaFieldAtPath,
  removeSchemaFieldAtPath,
  addSchemaFieldAtPath
} = useDomainModel()

const {
  toggleFieldFilterAtPath,
  toggleFieldComputedAtPath,
  blurFieldDescriptionAtPath
} = useReadModelActions()

const workflow = computed(() => store.state.workflow.workflow)

const hoveredPath = ref(null)

const isAddVisible = (row) => {
  if (!hoveredPath.value) {
    return false
  }

  return row.path.length <= hoveredPath.value.length &&
    row.path.every((value, index) => value === hoveredPath.value[index])
}

const rows = computed(() => {
  const result = []

  // Keys are name-based (stable across reorder) rather than index/path-based.
  // Index keys change on every reorder, which makes Vue's keyed diff drop
  // rows when Sortable has already mutated the DOM.
  const walk = (field, path, depth, ancestorBars, isLast, keyBase) => {
    result.push({
      type: 'field',
      key: `f|${keyBase}`,
      field,
      path,
      depth,
      ancestorBars,
      isLast
    })

    if (field.relatedEntityId && depth < props.maxDepth) {
      const childAncestorBars = depth >= 2 ? [...ancestorBars, !isLast] : []
      const children = field.fields ?? []
      children.forEach((childField, childIndex) => {
        walk(
          childField,
          [...path, childIndex],
          depth + 1,
          childAncestorBars,
          childIndex === children.length - 1,
          `${keyBase}|${childField.name}`
        )
      })
      result.push({
        type: 'add',
        key: `a|${keyBase}`,
        path,
        depth: depth + 1,
        ancestorBars: childAncestorBars
      })
    }
  }

  walk(props.topField, [props.fieldIndex], 1, [], props.topFieldIsLast, props.topField.name)

  return result
})

const rowClass = (depth) => {
  if (depth === 1) {
    return 'query-field-row'
  }

  return depth >= props.maxDepth ? 'query-subsubfield-row' : 'query-subfield-row'
}

const indentPx = (depth) => (depth - 1) * GUIDE + 'px'

const addIndentPx = (depth) => CELL_PADDING + (depth - 1) * GUIDE + 'px'

const connectorLines = (row) => {
  if (row.type !== 'field' || row.depth < 2) {
    return []
  }

  const lines = []
  const elbowColumn = row.depth - 2

  for (let column = 0; column <= elbowColumn; column++) {
    const x = CELL_PADDING + column * GUIDE + GUIDE / 2

    if (column < elbowColumn) {
      if (row.ancestorBars[column]) {
        lines.push({
          key: `t-${column}`,
          style: { left: x + 'px', top: 0, bottom: 0, width: '1px' }
        })
      }
      continue
    }

    lines.push({
      key: `v-${column}`,
      style: {
        left: x + 'px',
        top: 0,
        height: row.isLast ? '50%' : '100%',
        width: '1px'
      }
    })
    lines.push({
      key: `h-${column}`,
      style: { left: x + 'px', top: '50%', width: GUIDE / 2 + 'px', height: '1px' }
    })
  }

  return lines
}

const getRelatedEntity = (field) => {
  if (!field.relatedEntityId) {
    return null
  }

  return (workflow.value.schemas ?? []).find((s) => s.id === field.relatedEntityId)
}

const onBlurName = (e, path) => {
  const newName = e.target.innerText?.trim() ?? ''
  renameSchemaFieldAtPath(props.schema.id, path, newName)
}

const onBlurDescription = (e, field, path) => {
  const newDescription = (e.target.innerText || '').trim()
  const currentDescription = field.description || ''

  if (newDescription.length > MAX_FIELD_DESCRIPTION_LENGTH) {
    store.commit('snackbar/showMessage', {
      content: `Description must be at most ${MAX_FIELD_DESCRIPTION_LENGTH} characters`,
      timeout: 4000,
      color: 'red'
    }, { root: true })
    e.target.textContent = currentDescription

    return
  }

  blurFieldDescriptionAtPath(e, props.schema.id, path)
}

const onSelectRelatedEntity = (entity, path) => {
  selectRelatedEntityAtSchemaPath(entity, props.schema.id, path)
}

const onSetCardinality = (path, cardinality) => {
  setSchemaFieldCardinalityAtPath(props.schema.id, path, cardinality)
}

const onToggleFilter = (path) => {
  toggleFieldFilterAtPath(props.schema.id, path)
}

const onToggleComputed = (path) => {
  toggleFieldComputedAtPath(props.schema.id, path)
}

const onDelete = (path) => {
  removeSchemaFieldAtPath(props.schema.id, path)
}

const onAddSubField = (path) => {
  addSchemaFieldAtPath(props.schema.id, path)
}
</script>

<style scoped>
:deep(.entity-chip.v-chip) {
  height: 20px;
  font-size: 12px;
}

.name-cell {
  width: 240px;
  max-width: 240px;
  position: relative;
}

.tree-line {
  position: absolute;
  background-color: var(--light-4);
  pointer-events: none;
}

.description-cell {
  width: 320px;
  max-width: 320px;
}

.description-cell :deep(.field-description-input) {
  width: 100%;
}

.description-cell :deep(.field-description-input[contenteditable="true"]),
.description-cell :deep(.field-description-input[contenteditable="false"]) {
  font-style: italic;
  color: var(--text-light);
  font-size: 12px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  text-overflow: clip;
  overflow: visible;
}

.description-cell.cell-empty :deep(.field-description-input) {
  display: none;
}

.description-cell.cell-empty:hover :deep(.field-description-input),
.description-cell.cell-empty:focus-within :deep(.field-description-input) {
  display: block;
}

.description-cell.cell-empty:hover .cell-dash,
.description-cell.cell-empty:focus-within .cell-dash {
  display: none;
}

.entity-cell {
  width: 220px;
}

.cell-dash {
  color: var(--dark-1);
  font-size: 13px;
}

.entity-cell .entity-hover-reveal {
  display: none;
}

.query-field-row:hover .entity-cell .entity-hover-reveal,
.query-subfield-row:hover .entity-cell .entity-hover-reveal,
.query-subsubfield-row:hover .entity-cell .entity-hover-reveal {
  display: inline-block;
}

.query-field-row:hover .entity-cell .cell-dash,
.query-subfield-row:hover .entity-cell .cell-dash,
.query-subsubfield-row:hover .entity-cell .cell-dash {
  display: none;
}

.actions-cell {
  width: 48px;
  text-align: right;
  padding-right: 8px !important;
}

.query-fields-table :deep(tbody td.add-subfield-cell) {
  padding: 0 !important;
  height: 0 !important;
  border-bottom: none !important;
}

.add-subfield-wrapper {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  transition: max-height 0.2s ease, opacity 0.2s ease, padding 0.2s ease;
}

.add-subfield-wrapper--visible {
  max-height: 60px;
  opacity: 1;
  padding-top: 8px;
  padding-bottom: 12px;
}

.field-state-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-right: 4px;
  color: var(--text-light);
}

.handle-computed-badge {
  position: absolute;
  bottom: -1px;
  right: -1px;
  background: var(--light-1);
  border-radius: 50%;
}

:deep(.related-entity-chip) {
  position: relative;
}
</style>
